import type { ParserLink } from './parser/parser'
import type { Jurisdiction } from './jurisdiction'
import type { Source } from './norms'
import type { IContentType } from './ris.types'
import type {
  DecisionCategorization,
  DecisionSummary,
} from './judiciaryMonitoring/judiciaryMonitoring'

export const documentTypes = [
  'law',
  'clause',
  'decision',
  'rechtssatz',
  'gazette',
  'draft',
] as const
export type DocumentType = (typeof documentTypes)[number]

/** Court and authority documents, as opposed to legislation. */
export function isJudikatur(documentType: string): boolean {
  return documentType === 'decision' || documentType === 'rechtssatz'
}

/** A norm is a general abstract legal document */
export interface INorm<S extends Source = Source, M = unknown> {
  source: S
  /** Our internal ID */
  normId:
    | `eu.${string}`
    | `de.${string}`
    | `at.${string}`
    | `ro.${string}`
    | `es.${string}`
  /** Jurisdiction inferred from normId/source */
  jurisdiction?: Jurisdiction
  /** European Legislation Identifier (ELI) *OR* European Case Law Identifier (ECLI) */
  eli?: string
  title?: string
  longTitle?: string
  /** The date that the decision was published online (e.g. RIS or Findok) */
  publishedDate?: string
  changedDate?: string
  children?: IContentType[]
  sourceUrl?: string
  /** Source formats in which the document is available in, e.g. pdf or html */
  formats?: { pdf?: string; html?: string; xml?: string; epub?: string }
  documentType: DocumentType
  refs?: ParserLink[]
  /** S3 fallback if data is too large */
  s3Uri?: string
  documents?: NormDocument[]
  /** Provider-specific fields */
  sourceMetadata?: M
  keywords?: string[]
}

export type NormDocType = (typeof NORM_DOC_TYPES)[number]
export const NORM_DOC_TYPES = ['pdf', 'pdfa2a', 'html', 'xml', 'rtf'] as const

export function isNormDocType(value: string): value is NormDocType {
  return (NORM_DOC_TYPES as readonly string[]).includes(value)
}

export interface NormDocument {
  role: 'mainDocument' | 'material'
  title: string
  formats: Array<{
    type: NormDocType
    s3Uri?: string
    sourceUrl: string
  }>
}

/**
 * PDF manifestation types in preference order. A plain `pdf` is preferred, but
 * eurlex gazettes/laws that only ship a signed PDF expose it as `pdfa2a`
 * (DataType `Authentisch`), so it must be accepted as a valid PDF fallback.
 */
export const PDF_DOC_TYPES = ['pdf', 'pdfa2a'] as const satisfies NormDocType[]

/**
 * Resolves a document set's main PDF manifestation. Prefers the
 * `mainDocument` (falling back to the first document) and a plain `pdf` format
 * (falling back to the signed `pdfa2a`). Returns `undefined` when no PDF-like
 * manifestation is present.
 */
export function getMainDocumentPdfFormat(
  documents: NormDocument[] | null | undefined,
): NormDocument['formats'][number] | undefined {
  const mainDoc =
    documents?.find((doc) => doc.role === 'mainDocument') ?? documents?.[0]
  const formats = mainDoc?.formats
  if (!formats) return undefined
  for (const type of PDF_DOC_TYPES) {
    const format = formats.find((candidate) => candidate.type === type)
    if (format) return format
  }
  return undefined
}

/**
 * Fetchable URL of a document set's main PDF (see `getMainDocumentPdfFormat`).
 *
 * Where the format carries an `s3Uri`, that mirror is the answer: a source is
 * mirrored precisely because its own URL is not fetchable outside the ingest,
 * and juris' is the case that proves it — the law export answers `200` with 196
 * bytes of *"Ihre letzte Sitzung wurde bereits beendet"* rather than a PDF, so
 * the `sourceUrl` reads as populated while resolving to nothing. This is the
 * same preference `getAnyDocumentPdfUrl`'s scan already applies.
 */
export function getMainDocumentPdfUrl(
  documents: NormDocument[] | null | undefined,
): string | undefined {
  const format = getMainDocumentPdfFormat(documents)
  return format?.s3Uri ?? format?.sourceUrl
}

/**
 * Resolves a fetchable PDF for a document set, scanning every document — state
 * gazettes persisted before the PDF moved onto their `mainDocument` carry it on
 * the per-change `material` attachments instead. The main document keeps
 * priority (unchanged behavior for at.ris/de.ris/eurlex); in the fallback scan
 * the S3 mirror wins over the source URL, which on the juris portals is
 * session-bound and unfetchable outside the ingest.
 */
export function getAnyDocumentPdfUrl(
  documents: NormDocument[] | null | undefined,
): string | undefined {
  const main = getMainDocumentPdfUrl(documents)
  if (main) return main
  for (const doc of documents ?? []) {
    for (const type of PDF_DOC_TYPES) {
      const format = doc.formats?.find((entry) => entry.type === type)
      if (format) return format.s3Uri ?? format.sourceUrl
    }
  }
  return undefined
}

/**
 * Our mirror of a document set's PDF, scanning every document — state gazettes
 * persisted before the PDF moved onto their `mainDocument` carry theirs on the
 * per-change `material` attachments instead.
 *
 * Unlike {@link getAnyDocumentPdfUrl} this never falls back to `sourceUrl`: callers
 * use it to decide whether a mirror exists at all, which is what tells a broken
 * source link (juris' session-bound attachment) apart from a working one.
 */
export function getDocumentPdfS3Uri(
  documents: NormDocument[] | null | undefined,
): string | undefined {
  for (const doc of documents ?? []) {
    for (const type of PDF_DOC_TYPES) {
      const s3Uri = doc.formats?.find((entry) => entry.type === type)?.s3Uri
      if (s3Uri) return s3Uri
    }
  }
  return undefined
}

/**
 * The app-origin url of a gazette's mirrored PDF. Opaque by design: the bucket,
 * region and key stay server-side, and the href stays stable, so an RSC payload
 * the browser replays on back-navigation cannot go stale the way a presigned url
 * does.
 *
 * @param options.page a page of the gazette PDF to open at — a PDF Open
 *   Parameter, so it is honoured by the browser viewers and harmlessly ignored
 *   by anything else.
 * @param options.origin the dashboard's own origin, for a link that leaves the
 *   app: nothing resolves a site-relative path from inside a workbook or a mail
 *   client. Prefer {@link gazettePdfExportUrl}, which picks the right url for
 *   that case.
 */
export function gazettePdfHref(
  normId: string,
  options: { page?: number; origin?: string } = {},
): string {
  const { page, origin = '' } = options
  const path = `${origin}/api/gazette-pdf/${encodeURIComponent(normId)}`
  return page ? `${path}#page=${page}` : path
}

/**
 * Whether a path segment could be a normId {@link gazettePdfHref} encoded. The
 * route checks this before it reaches the database, so an unauthenticated
 * request with a junk id is refused without a query.
 *
 * A shape bound, not an allowlist: this route serves every source, whose gazette
 * normIds range from a bare AT number to `de.bw.lw%2Fkultg-bw` (already
 * percent-encoded when stored, hence the literal `%`). It rejects what our
 * ingestion could never have written; it does not claim to reject every id that
 * has no row — the cacheable 404 is what absorbs those.
 */
export function isGazettePdfNormId(normId: string): boolean {
  return /^[A-Za-z0-9._%/-]{1,128}$/.test(normId)
}

/**
 * The publisher's own PDF url for a document set, scanning every document and
 * never our mirror — the link to hand someone who may never reach our origin.
 * `pdf` before the signed `pdfa2a`, per document, so a eurlex gazette shipping
 * only the signed manifestation still resolves.
 */
export function getDocumentPdfSourceUrl(
  documents: NormDocument[] | null | undefined,
): string | undefined {
  for (const doc of documents ?? []) {
    for (const type of PDF_DOC_TYPES) {
      const sourceUrl = doc.formats?.find(
        (entry) => entry.type === type,
      )?.sourceUrl
      if (sourceUrl) return sourceUrl
    }
  }
  return undefined
}

/**
 * A gazette's PDF for a link that outlives the app session — an Excel export, a
 * digest email. Absolute, and pointed at whichever copy the recipient can
 * actually fetch: our route where we hold a mirror, the publisher's own url
 * where we don't.
 *
 * The mirror decides, not the source: only the juris Landesrecht portals
 * (BE/BW/SH) serve a PDF bound to the session that opened the document, and
 * those are exactly the ones we mirror. Keeping the official url everywhere
 * else leaves an at.ris/de.ris/eurlex link working on its own, with no hop
 * through us and nothing to break if the dashboard is down.
 *
 * @param origin the dashboard's own origin — `getDomainFor('codara-dashboard')`.
 */
export function gazettePdfExportUrl(
  gazette: {
    normId: string
    documents: NormDocument[] | null | undefined
  },
  origin: string,
): string | undefined {
  if (getDocumentPdfS3Uri(gazette.documents)) {
    return gazettePdfHref(gazette.normId, { origin })
  }
  return getDocumentPdfSourceUrl(gazette.documents)
}

/**
 * The same document set with our S3 mirrors dropped, for handing to the browser:
 * the client is given {@link gazettePdfHref} instead, so the bucket url has no
 * business crossing into an RSC payload.
 */
export function withoutS3Uris(
  documents: NormDocument[] | null | undefined,
): NormDocument[] | null {
  if (!documents) return null
  return documents.map((doc) => ({
    ...doc,
    formats: doc.formats?.map(({ s3Uri: _s3Uri, ...format }) => format),
  }))
}

/**
 * Flattens a document set into a per-type source-URL record (`INorm['formats']`
 * shape) for rendering download links. Document order wins: the first URL seen
 * for each type is kept. Applies to any `NormDocument[]` — laws and gazettes
 * alike.
 */
export function getDocumentFormatUrls(
  documents: NormDocument[] | null | undefined,
): NonNullable<INorm['formats']> {
  const urls: NonNullable<INorm['formats']> = {}
  for (const doc of documents ?? []) {
    for (const format of doc.formats ?? []) {
      const type = format.type
      if (
        (type === 'pdf' || type === 'html' || type === 'xml') &&
        !urls[type]
      ) {
        // Deliberately the source url, not the `s3Uri` mirror that
        // `getMainDocumentPdfUrl` prefers: this scan reaches the `material`
        // attachments too, where the state platforms' own pdf urls are public
        // and fetchable, and swapping those for our copies would replace a
        // working official link on 115 persisted gazettes.
        urls[type] = format.sourceUrl
      }
    }
  }
  return urls
}

/** Normalizes old RIS document format (Name/Urls/ContentType) to NormDocument */
export function normalizeDocument(
  doc: NormDocument | Record<string, any>,
): NormDocument {
  if ('role' in doc && 'formats' in doc) return doc as NormDocument

  // Old parlament.gv attachment format: {title, documents: [{link, type}]}
  if ('title' in doc && 'documents' in doc && Array.isArray(doc.documents)) {
    return {
      role: 'material',
      title: doc.title ?? '',
      formats: doc.documents.map((d: { link: string; type?: string }) => ({
        type: (d.type?.toLowerCase() ?? 'pdf') as NormDocType,
        sourceUrl: d.link,
      })),
    }
  }

  const pdfUrl = doc.Urls?.Url ?? doc.Urls?.ContentUrl?.Url
  return {
    role: doc.ContentType ?? '',
    title: doc.Name ?? '',
    formats: pdfUrl ? [{ type: 'pdf' as NormDocType, sourceUrl: pdfUrl }] : [],
  }
}

export type ParsedSectionTitle = {
  enumeration?: string
  title?: string
  level: number
}

export type ParsedClause = {
  enumeration?: string
  title?: string
  children: IContentType[]
  sectionTitles?: ParsedSectionTitle[]
  clauseId?: string
  /** ISO `yyyy-MM-dd`; sources that version clauses individually set these. */
  inEffectDate?: string
  outOfEffectDate?: string
}

/** A clause, used in legal documents. Can be a paragraph, article or attachment */
export interface IClause<S extends Source = Source, M = unknown> extends INorm<
  S,
  M
> {
  /** @deprecated */
  id?: string
  clauseId: string
  category?: string
  tableOfContents?: IContentType[]
  inEffectDate?: string
  outOfEffectDate?: string
  abbreviation?: string
  altAbbreviations?: string[]
  citation?: string
  // /**
  //  * Additional number to differentiate norms with the same abbreviation. `null` means that the norm should not have a abbrNr in the URL
  //  */
  // abbrNr?: number | null
  sectionTitles?: ParsedSectionTitle[]
  enumeration: string
  /** @deprecated Use `counts.decision` instead */
  decisionsCount?: number
  /** @deprecated Use `counts.decision` instead */
  entscheidungenCount?: number
  contractParties?: string[]
  index?: number
  views?: number
  /** Stats about refs pointing to this document */
  counts?: Partial<Record<DocumentType, number>>
  institution?: string
}

export interface ILaw<
  S extends Source = Source,
  M = unknown,
  C = unknown,
> extends INorm<S, M> {
  clauses: Array<IClause<S, C>>
  title: string // title required here
  longTitle?: string
  abbreviation?: string
  altAbbreviations?: string[]
  tableOfContents?: IContentType[]
  inEffectDate?: string
  outOfEffectDate?: string
  lawType?: string
  /**
   * Additional number to differentiate norms with the same abbreviation. `null` means that the norm should not have a abbrNr in the URL
   */
  abbrNr?: number | null
  citation?: string
  contractParties?: string[]
}

export interface IDecision<
  S extends Source = Source,
  M = unknown,
> extends INorm<S, M> {
  decisionDate?: string
  institution?: string
  caseNumber?: string
  altCaseNumbers?: string[]
  summary?: DecisionSummary
  categorization?: DecisionCategorization
  legalCategories?: string[]
  decisionType?: string
}

export type LawChangeRelation =
  | 'modified'
  | 'corrigendum'
  | 'repealed'
  | 'implicitly-repealed'
  | 'extended-validity'
  | 'completed'
  | 'new'

export interface LawChange {
  normId: string
  title: string
  lawType?: string
  /**
   * The PG `gazette_law_changes.id` this change was derived from. Threaded
   * through from `handleGazetteChanges` so reports can persist
   * `legal_field_law_changes` rows that FK-reference the canonical change.
   * Optional: legacy DDB change items written before the PG migration lack it.
   */
  gazetteLawChangeId?: string
  status?: LawChangeRelation
  match?: {
    matchedBy: 'title'
    matchScore: number
  }
  description?: string
  summary?: string
  pageNumbers?: number[]
  recommendations?: string[]
  relevancy?: number
  /** Pre-amendment clause snapshot (eurlex consolidated PDF URL) */
  oldClause?: string
  clauses: Array<{
    enumeration: string
    title?: string
    /**
     * Two shapes depending on source, and consumers discriminate on
     * `typeof === 'string'` (see `GazetteModel.extractContentOldRis` /
     * `extractContentEurlex`):
     * - `IClause` — at.ris: the pre/post-amendment clause snapshot.
     * - `string` — eu.eurlex: the consolidated PDF URL to diff against.
     * - absent — labels only; the source resolves the predecessor at read
     *   time instead of storing it.
     *
     * Entry kinds and `null`: see `GazetteLawChangeClause`.
     *
     * Mirrors `GazetteLawChangeClause` in `@shared/db/schema/gazettes`, which
     * is what these get persisted as.
     */
    oldClause?: string | IClause | null
    newClause?: string | IClause | null
    /** Per-clause status when the gazette is post-processed */
    status?: LawChangeRelation
  }>
}

export interface IGazette<S extends Source = Source, M = unknown> extends INorm<
  S,
  M
> {
  title: string
  longTitle?: string
  gazetteNr: string
  changes: LawChange[]
  state?: string
  legislationDate?: string
}
